import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import React, { useState } from 'react';

import { FormSetup } from '@/components/Form/FormSetup';
import { FormSetupConfirm } from '@/components/Form/FormSetupConfirm';
import { iSetupData } from '@/components/PublicPages/SetupPublicPage';
import { PHONE_USER_CONSENT_CHECKBOX } from '@/config';
import { useRequestMobileToken, useValidateMobileToken } from '@/features/mobileToken';
import { useMe } from '@/hooks/useMe';
import { buildGTMTokenRequest, buildGTMTokenValidated, sendEventGtm } from '@/lib/gtm/gtmUtils';

export type SetupContainerProps = {
  initialValues: iSetupData;
  successRedirectURL: string;
};

export const SetupContainer = ({ initialValues, successRedirectURL }: SetupContainerProps) => {
  const [showFormSetup, setShowFormSetup] = useState(true);
  const [showFormSetupConfirm, setShowFormSetupConfirm] = useState(false);
  const [submittingConfirm, setSubmittingConfirm] = useState(false);
  const [submittingSetup, setSubmittingSetup] = useState(false);
  const [systemError, setSystemError] = useState({});
  const requestMobileTokenMutation = useRequestMobileToken();
  const validateMobileTokenMutation = useValidateMobileToken();
  const [systemConfirmError, setSystemConfirmError] = useState({});
  const [userBlocked, setUserBlocked] = useState(false);
  const [systemConfirmSuccess, setSystemConfirmSuccess] = useState(false);
  const [tokenResent, setTokenResent] = React.useState(false);
  const [tokenResentError, setTokenResentError] = React.useState(false);
  const [tokenLoading, setTokenLoading] = React.useState(false);
  const sendDataToGTM = useGTMDispatch();
  const { getMe } = useMe();

  const sendDataForRetrieveToken = async (values: any) => {
    const { hash, internationalPhone: phone, recaptcha } = values;

    return await requestMobileTokenMutation.mutateAsync({
      hash,
      phone,
      recaptcha,
    });
  };

  const sendDataForValidateToken = async (values: any) => {
    const { hash, internationalPhone: phone, token } = values;
    return await validateMobileTokenMutation.mutateAsync({
      hash,
      phone,
      token,
    });
  };

  const onDefaultResendToken = () => {
    setSystemConfirmError({});
    setTokenResent(false);
    setTokenResentError(false);

    sendDataForRetrieveToken(initialValues).then((response: any) => {
      setTokenLoading(false);

      if (response.validAt) {
        setTokenResent(true);
      } else {
        setTokenResentError(true);
      }
    });
  };

  const onDefaultFormSetupSubmit = (values: iSetupData) => {
    initialValues = Object.assign(initialValues, values);

    setSubmittingSetup(true);
    setSystemError({});

    sendDataForRetrieveToken(values).then((response: any) => {
      setSubmittingSetup(false);
      if (response.validAt) {
        onSubmitSetupSuccess();
      } else {
        onSubmitSetupError(response);
      }
    });
  };

  const onSubmitSetupSuccess = () => {
    // Animated Scroll to top (it seems to work only in Chrome)
    // I have tried with a polyfill, but it doesn't work on Edge...
    window.scrollTo({ top: 0, behavior: 'smooth' });

    sendEventGtm(buildGTMTokenRequest(getMe()), sendDataToGTM);
    setSubmittingSetup(true);
    setShowFormSetup(false);
    setShowFormSetupConfirm(true);

    window.history.replaceState(null, '', '/setup-confirm');
  };

  const onSubmitSetupError = (response: any) => {
    setSystemError(response);
  };

  const onDefaultFormSetupConfirmSubmit = (values: iSetupData) => {
    setSystemConfirmError({});

    validateToken(values).then((response: any) => {
      setSubmittingConfirm(false);

      if (!response.response) {
        if (response.status === 429) {
          setUserBlocked(true);
        } else {
          setSystemConfirmError({
            detail: 'errorToken',
            status: 406,
          });
        }

        return;
      }

      sendEventGtm(buildGTMTokenValidated(getMe()), sendDataToGTM);
      setSystemConfirmError({});
      setUserBlocked(false);
      setSystemConfirmSuccess(true);
    });
  };

  const validateToken = (tokenData: iSetupData) => {
    setSubmittingConfirm(true);

    return sendDataForValidateToken(tokenData);
  };

  const FormSetupWrapper = () => {
    initialValues.formName = 'Setup';

    return (
      <FormSetup
        onSubmit={onDefaultFormSetupSubmit}
        initialValues={initialValues}
        isSubmitting={submittingSetup}
        systemError={systemError}
        showAgreementsCheck={PHONE_USER_CONSENT_CHECKBOX}
      />
    );
  };

  const onGoBack = () => {
    setSubmittingSetup(false);
    setSubmittingConfirm(false);
    setShowFormSetupConfirm(false);
    setShowFormSetup(true);
    setSystemError({});
    setSystemConfirmError({});
    setSystemConfirmSuccess(false);
    setTokenResent(false);
    setTokenResentError(false);
    setTokenLoading(false);

    window.history.replaceState(null, '', '/setup');
  };

  const FormSetupConfirmWrapper = () => {
    initialValues.formName = 'TokenCheck';

    return (
      <FormSetupConfirm
        onSubmit={onDefaultFormSetupConfirmSubmit}
        onResendToken={onDefaultResendToken}
        initialValues={initialValues}
        isSubmitting={submittingConfirm}
        onGoBack={onGoBack}
        userBlocked={userBlocked}
        systemError={systemConfirmError}
        actionForm={successRedirectURL}
        systemConfirmSuccess={systemConfirmSuccess}
        tokenResent={tokenResent}
        tokenResentError={tokenResentError}
        tokenLoading={tokenLoading}
      />
    );
  };

  return (
    <div>
      {showFormSetup && <FormSetupWrapper />}
      {showFormSetupConfirm && <FormSetupConfirmWrapper />}
    </div>
  );
};
